import {
  parseBoatDocument,
  sanitizeBoatFilename,
  serializeBoat,
  type BoatDocument,
} from '../../sim/boat/index.ts';

const STORAGE_KEY = 'battleboats:boats:draft';
const FILE_NAME_KEY = 'battleboats:boats:file';
const DIRTY_KEY = 'battleboats:boats:dirty';
const DIRTY_VALUE = '1';
const REPO_SAVE_PATH = '/__battleboats/save-boat';
const REPO_DELETE_PATH = '/__battleboats/delete-boat';

export type ResumableBoatDraft = {
  boat: BoatDocument;
  fileName: string | null;
};

export function saveBoatDraft(boat: BoatDocument, fileName: string | null): void {
  try {
    localStorage.setItem(STORAGE_KEY, serializeBoat(boat));
    localStorage.setItem(DIRTY_KEY, DIRTY_VALUE);

    if (fileName) {
      localStorage.setItem(FILE_NAME_KEY, fileName);
    } else {
      localStorage.removeItem(FILE_NAME_KEY);
    }
  } catch {
    // Ignore storage failures; autosave is best-effort.
  }
}

export function clearBoatDraft(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(FILE_NAME_KEY);
    localStorage.removeItem(DIRTY_KEY);
  } catch {
    // Ignore storage failures; autosave is best-effort.
  }
}

export function loadBoatDraft(): ResumableBoatDraft | null {
  try {
    if (localStorage.getItem(DIRTY_KEY) !== DIRTY_VALUE) {
      return null;
    }

    const raw = localStorage.getItem(STORAGE_KEY);
    const boat = raw ? parseBoatDocument(raw) : null;

    if (!boat) {
      return null;
    }

    const stored = localStorage.getItem(FILE_NAME_KEY);
    return { boat, fileName: stored ? sanitizeBoatFilename(stored) : null };
  } catch {
    return null;
  }
}

export function canWriteRepoBoat(): boolean {
  return import.meta.env.DEV;
}

export async function writeRepoBoat(filename: string, contents: string): Promise<boolean> {
  const name = sanitizeBoatFilename(filename);

  if (!name) {
    return false;
  }

  try {
    const response = await fetch(REPO_SAVE_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: name, contents }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function deleteRepoBoat(filename: string): Promise<boolean> {
  const name = sanitizeBoatFilename(filename);

  if (!name) {
    return false;
  }

  try {
    const response = await fetch(REPO_DELETE_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: name }),
    });
    return response.ok || response.status === 404;
  } catch {
    return false;
  }
}

/** Preview and production cannot write `boats/`. Offer a download instead. */
export function downloadBoatFile(boat: BoatDocument, filename: string): void {
  const blob = new Blob([serializeBoat(boat)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export type PickBoatJsonResult =
  | { status: 'picked'; json: string; fileName: string }
  | { status: 'cancelled' }
  | { status: 'unreadable' };

/** Native JSON picker. Callers accept the text only through `importBoatDocument`. */
export function pickBoatJsonFile(): Promise<PickBoatJsonResult> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.style.display = 'none';
    let settled = false;

    const finish = (value: PickBoatJsonResult): void => {
      if (settled) {
        return;
      }

      settled = true;
      input.remove();
      resolve(value);
    };

    input.addEventListener('change', () => {
      const file = input.files?.[0];

      if (!file) {
        finish({ status: 'cancelled' });
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        const json = typeof reader.result === 'string' ? reader.result : '';
        finish({ status: 'picked', json, fileName: file.name });
      };
      reader.onerror = () => {
        finish({ status: 'unreadable' });
      };
      reader.readAsText(file);
    });
    input.addEventListener('cancel', () => {
      finish({ status: 'cancelled' });
    });

    document.body.append(input);
    input.click();
  });
}
